"use client"

import { useEffect, useId, useState } from "react"

import {
    AccordionContent,
    AccordionIcon,
    AccordionRoot,
    AccordionTrigger
} from "@/app/(site)/_components/Atoms/Accordion"
import AccordionToggleIcon from "@/app/(site)/_components/GCMolecules/AccordionElement/AccordionToggleIcon"
import type { CategoryTranslations } from "@/app/(site)/_components/Organisms/ProductGrids/_utils/categoryTranslations"
import { sortConfig } from "@/app/(site)/_components/Organisms/ProductGrids/types/options"
import {
    useProductGridContext,
    useStaticGridContext
} from "@/app/(site)/contexts/product-grid/ProductGridContext"
import { FOCUS_VISIBLE_BORDER_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"

const sortLabelMap: Record<string, keyof CategoryTranslations> = {
    Recommended: "recommended",
    "Price, high to low": "priceHighToLow",
    "Price, low to high": "priceLowToHigh",
    "Newest first": "newestFirst"
}

export function getSortLabel(
    title: string | undefined,
    translations: CategoryTranslations
): string {
    if (!title) return ""
    const key = sortLabelMap[title]
    return key ? translations[key] : title
}

type SortItemProps = {
    sortKey: string
    field: string
    meta: (typeof sortConfig)[string]["meta"]
    isChecked: boolean
    categoryTranslations: CategoryTranslations
    onClearSort: () => void
    onSetSort: (key: string) => void
}

function SortItem({
    sortKey,
    field,
    meta,
    isChecked,
    categoryTranslations,
    onClearSort,
    onSetSort
}: SortItemProps) {
    const instanceId = useId()
    const inputId = `${instanceId}-${field}-${meta?.title ?? sortKey}`
    const [checked, setChecked] = useState(isChecked)

    useEffect(() => {
        setChecked(isChecked)
    }, [isChecked])

    const handleChange = () => {
        if (sortKey !== "_relevance") setChecked(checked => !checked)
        if (sortKey === "_relevance") onClearSort()
        else if (checked) onClearSort()
        else onSetSort(sortKey)
    }

    return (
        <label
            htmlFor={inputId}
            data-checked={checked}
            className={clsx(
                "group/sort relative -mt-px flex h-44 w-full cursor-pointer items-center justify-between border border-col-border-neutral px-12 first:mt-0",
                "transition-[border-color] duration-200 ease-in-out",
                "lg:hover:z-10 lg:hover:border-col-border-subtle",
                "data-[checked=true]:bg-col-bg-calm",
                FOCUS_VISIBLE_BORDER_CLASS
            )}
        >
            <input
                id={inputId}
                type="checkbox"
                className="absolute inset-0 cursor-pointer opacity-0"
                onChange={handleChange}
                onKeyDown={event => {
                    if (event.key !== "Enter") return
                    event.preventDefault()
                    handleChange()
                }}
                checked={checked}
                aria-label={`Sort by ${meta?.title}`}
            />
            <span className="pt-3 Trade_Gothic_Text_14_150_0_300">
                {getSortLabel(meta?.title, categoryTranslations)}
            </span>
            <span
                className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-col-text-primary group-data-[checked=true]/sort:bg-col-text-primary"
                aria-hidden="true"
            >
                <span className="block h-6 w-6 rounded-full bg-white group-data-[checked=false]/sort:hidden" />
            </span>
        </label>
    )
}

export function Sort({ initialOpen = true }: { initialOpen?: boolean }) {
    const { filterState, setSort, clearSort } = useProductGridContext()
    const { categoryTranslations } = useStaticGridContext()

    return (
        <AccordionRoot
            accordionId="sort"
            accordionName="Sort"
            initalState={initialOpen}
            hydratedState={initialOpen}
            className={clsx(
                "relative -mr-px -mt-px border border-col-border-neutral border-r-transparent first:mt-0",
                "data-[open=false]:lg:hover:z-10 data-[open=false]:lg:hover:border-col-border-subtle",
                "transition-[border-color] duration-200 ease-in-out"
            )}
        >
            <AccordionTrigger className="flex h-58 w-full items-center justify-between py-8 pl-12 pr-0 lg:pl-24 lg:pr-12">
                <span className="pt-3 Trim_Mono_POC_Text_14_130_-5_500_uppercase">
                    {categoryTranslations.sortBy}
                </span>
                <AccordionIcon
                    openIcon={<AccordionToggleIcon isOpen={true} />}
                    closeIcon={<AccordionToggleIcon isOpen={false} />}
                    className="flex h-44 w-44 items-center justify-center"
                />
            </AccordionTrigger>
            <AccordionContent>
                <div
                    className="flex flex-col px-12 pb-40 pt-4 lg:px-24"
                    role="radiogroup"
                    aria-label={categoryTranslations.sortBy}
                >
                    {Object.entries(sortConfig).map(([key, { field, meta }]) => {
                        const isChecked =
                            (filterState.sort?.field === field &&
                                filterState.sort?.order === meta?.values?.[0]) ||
                            (!filterState.sort && key === "_relevance")
                        return (
                            <SortItem
                                key={`${field}-${meta?.title}`}
                                sortKey={key}
                                field={field}
                                meta={meta}
                                isChecked={isChecked}
                                categoryTranslations={categoryTranslations}
                                onClearSort={clearSort}
                                onSetSort={setSort}
                            />
                        )
                    })}
                </div>
            </AccordionContent>
        </AccordionRoot>
    )
}
